import axios from "axios";
// import ImageCacheHelper from "./image-cache-helper";
import { NO_PHOTO, LOADING_IMAGE } from "../common/constants";
import ImageLazyLoader from "./directives/image-lazy-loader";
import fileUriConst from "../common/uri/file-uri.constants";
import { TIMESTAMP_MARKERS } from "./image-url";
import { isAbsoluteUrl } from "./util";

import { ENABLE_RETRY_CLOUD_IMAGE, MAX_NUMBER_RETRY_GET_IMAGE } from './app-config'

export default class ImageHelper {

    public static getPublishLink(imageUrl) {
        return axios.get(imageUrl);
    }

    private static retryLoadImage(elm, imageUrl) {
        const retryAttr = 'had-retry';
        const hadRetry = elm.getAttribute(retryAttr);
        if (!hadRetry || parseInt(hadRetry) < MAX_NUMBER_RETRY_GET_IMAGE) {
            ImageHelper.setImage(elm, imageUrl);
            const retryValue = hadRetry ? parseInt(hadRetry) + 1 : 1;
            console.log('image retry: ', retryValue)
            elm.setAttribute(retryAttr, retryValue);
        }
    }

    /**
    * imageInfo: { mode: "large|small", width:null|xx|-1(auto), height:null|xx|-1(auto), scale:null|auto|down|up, quality:null|1-100 }
    */
    public static loadPublishLink(fileName, imgInfo, element, callback = null) {
        element.setAttribute("lazy-status", "1");

        let imageUrl = this.buildImageUrl(fileName, imgInfo);
        // var cachedPublishUrl = ImageCacheHelper.get(imageUrl + element.getAttribute("cached-id"));

        // if (cachedPublishUrl) {
        //     ImageHelper.setImage(element, cachedPublishUrl);
        //     if (callback) {
        //         callback(true, cachedPublishUrl);
        //     }
        // }
        // else {
            
        // }
        element.src = LOADING_IMAGE;
        element.style = "object-fit: scale-down"
        ImageHelper.getPublishLink(imageUrl).then(function (resp) {
            const cloudImageUrl = resp.data.data.URL;
            // ImageCacheHelper.set(imageUrl + element.getAttribute('cached-id'), cloudImageUrl);
            ImageHelper.setImage(element, cloudImageUrl);
            element.style = ""
            element.setAttribute("lazy-status", "2");
            if (ENABLE_RETRY_CLOUD_IMAGE) {
                element.onerror = () => ImageHelper.retryLoadImage(element, cloudImageUrl);
            }

            ImageLazyLoader.disableEvent(element);
            if (callback) {
                callback(true, imageUrl);
            }
        })
            .catch(function (err) {
                ImageHelper.setImage(element, NO_PHOTO);
                element.setAttribute("lazy-status", "2");
                element.style = ""
                ImageLazyLoader.disableEvent(element);
                if (callback) {
                    callback(false);
                }
            });
    }

    static checkTime(url) {
        return Boolean(url) && TIMESTAMP_MARKERS.some(marker => url.includes(marker));
    }

    static setImage(el, url) {
        if (url === LOADING_IMAGE) {
            el.style = "object-fit: scale-down";
        }
        // Raw url on purpose, exactly what the old setImage ended up assigning:
        // its trailing `if (!el.url) el.src = url` always ran on an <img>, so its
        // custom_time was overwritten. Cache-busting here would refetch every
        // untimestamped publish url app-wide on each call.
        el.src = url;
    }

    static buildImageUrl(fileName: string, imgInfo: any): string {
        let info = { ...{ width: null, height: null, scale: null, quality: null }, ...imgInfo };

        // default width|height
        if (!info.width && !info.height) {
            switch (info.mode) {
                case 'small':
                    info.width = 100;
                    info.height = 100;
                    break;

                case 'large':
                default:
                    // info.width = 800;
                    // info.height = 600;
                    break;
            }
        }

        // quality
        if (!info.quality) {
            switch (info.mode) {
                case 'small':
                    info.quality = 80;
                    break;

                case 'large':
                default:
                    info.quality = 100;
                    break;
            }
        }

        // scale
        if (info.width && info.height)
            info.scale = 'down';

        // fix width|height
        if (info.width && !info.height) info.height = -1; //auto
        if (!info.width && info.height) info.width = -1; //auto

        // render url
        let imageUrl = `/${(info.mode === "small" ? fileUriConst.getSmallPublishPictureLink : fileUriConst.getPublishPictureLink).replace(":fileName", fileName)}`;

        if (info.width)
            imageUrl = `${imageUrl}&w=${info.width}`;

        if (info.height)
            imageUrl = `${imageUrl}&h=${info.height}`;

        if (info.scale)
            imageUrl = `${imageUrl}&s=${info.scale}`;

        if (info.quality)
            imageUrl = `${imageUrl}&q=${info.quality}`;

        //console.log(imageUrl)
        return imageUrl;
    }

    /**
     * Best-quality source url of a picture, for flows that re-process the image
     * (AI editing, rotation). OriginalPublishUrl serves the stored image without
     * any scale/quality effect (SWA-7482); the fallbacks cover pictures loaded by
     * paths that don't populate it.
     */
    static getSourceImageUrl(pic: any): string | undefined {
        return pic?.OriginalPublishUrl || pic?.XLargeUrl || pic?.LargeUrl || pic?.URL;
    }

    static async rotateImageToFile(imageUrl: string, degrees: number, baseName: string): Promise<File> {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        try {
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = () => reject(new Error("Failed to load image for rotation"));
                image.src = objectUrl;
            });

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const normalized = ((degrees % 360) + 360) % 360;
            const swap = normalized === 90 || normalized === 270;
            canvas.width = swap ? img.height : img.width;
            canvas.height = swap ? img.width : img.height;

            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((normalized * Math.PI) / 180);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            const mimeType = blob.type && blob.type.startsWith("image/") && blob.type !== "image/heic" ? blob.type : "image/jpeg";
            const rotatedBlob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((b) => b ? resolve(b) : reject(new Error("Failed to encode rotated image")), mimeType, 0.95);
            });

            const extension = mimeType === "image/jpeg" ? "jpg" : mimeType.replace(/^image\//, "");
            return new File([rotatedBlob], `${baseName}.${extension}`, { type: mimeType });
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    }

    static populateImageUrl(el: any, model: any, defIfInvalid: boolean, callback?: any) {
        let fileName = null;
        let isPublishUrl = false;
        const isDirect = el.hasAttribute("loader-direct");
        const imgMode = el.getAttribute("image-size") || "large";
        const imgWidth = el.getAttribute("image-width");
        const imgHeight = el.getAttribute("image-height");

        if (isDirect) {
            fileName = model;
            isPublishUrl = isAbsoluteUrl(model); // already a publish url

        } else if (model.ImageFileNames && model.ImageFileNames.length > 0) {
            fileName = model.ImageFileNames[0];

        } else if (model.SmallUrl || model.OriginalUrl) {
            fileName = imgMode == "small" ? model.SmallUrl : model.OriginalUrl;
        }

        // BE return directly publish-url
        if (model.SmallPublishUrl || model.OriginalPublishUrl || model.PictureXLargeUrl) {
            isPublishUrl = true;
            fileName = imgMode == "small" ? model.SmallPublishUrl : (model.PictureXLargeUrl || model.OriginalPublishUrl);
        }
        if (model.IsMissingOnCloud || fileName === undefined || !fileName || fileName == "null" || fileName == '') {
            if (defIfInvalid) {
                ImageHelper.setImage(el, NO_PHOTO);
            }
            return;
        }

        if (isPublishUrl) {
            ImageHelper.setImage(el, fileName);
            if (ENABLE_RETRY_CLOUD_IMAGE) {
                el.onerror = () => this.retryLoadImage(el, fileName);
            }
        } else {
            ImageHelper.loadPublishLink(fileName, { mode: imgMode, width: imgWidth, height: imgHeight }, el, callback);
        }
    }
}
